import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setIntakePayload } from '../state/governmentIntakeSlice';
import { selectIntakePayload } from '../state/selectors';
import {
  createEmptyProgramSubSections,
  createEmptyProjectsSubSections,
  createEmptyPropertiesSubSection,
  EMPTY_CURRENT_SYSTEMS,
  EMPTY_ENTRY_CONTACT,
  EMPTY_INTAKE_PAYLOAD,
  EMPTY_ROLE_CONTEXT,
  type EntryContactInfo,
  type IntakePayload,
  type NonSystemTools,
  type PrimaryObjectOfWorkItem,
  type PrimaryObjectOfWorkSectionKey,
  type ProgramSubSections,
  type ProjectsSubSections,
  type PropertiesSubSections,
  type RoleContext,
  type SystemData,
} from '../types/intake.types';

export type IntakeFormContextValue = {
  data: IntakePayload & {
    programs?: ProgramSubSections;
    projects?: ProjectsSubSections;
    properties?: PropertiesSubSections;
  };
  updateEntryContact: (v: EntryContactInfo) => void;
  updateRoleContext: (v: RoleContext) => void;
  addSystem: (v: SystemData) => void;
  updateSystem: (id: number, v: SystemData) => void;
  removeSystem: (id: number) => void;
  setNonSystemTools: (v: NonSystemTools) => void;
  setPrimaryObjectOfWork: (v: string[]) => void;
  updatePrimaryObjectOfWorkSubSection: <K extends keyof ProgramSubSections>(
    sectionKey: PrimaryObjectOfWorkSectionKey,
    subSectionKey: K,
    value: ProgramSubSections[K]
  ) => void;
  updateProgramSubSection: <K extends keyof ProgramSubSections>(key: K, value: ProgramSubSections[K]) => void;
  updateProjectsSubSection: <K extends keyof ProjectsSubSections>(key: K, value: ProjectsSubSections[K]) => void;
  updatePropertiesSubSection: <K extends keyof PropertiesSubSections>(key: K, value: PropertiesSubSections[K]) => void;
  getPayload: () => IntakePayload;
  getPayloadUpToStep: (stepIndex: number, stepKeys: string[]) => IntakePayload;
};

export function useIntakeForm(): IntakeFormContextValue {
  const dispatch = useDispatch();
  const payloadFromRedux = useSelector(selectIntakePayload);
  const data = payloadFromRedux ?? EMPTY_INTAKE_PAYLOAD;
  const dataRef = useRef(data);
  dataRef.current = data;

  const updateEntryContact = useCallback(
    (v: EntryContactInfo) => {
      dispatch(setIntakePayload({ ...dataRef.current, entryContact: v }));
    },
    [dispatch]
  );

  const updateRoleContext = useCallback(
    (v: RoleContext) => {
      dispatch(setIntakePayload({ ...dataRef.current, roleContext: v }));
    },
    [dispatch]
  );

  const addSystem = useCallback(
    (v: SystemData) => {
      const p = dataRef.current;
      const cs = p.currentSystems ?? EMPTY_CURRENT_SYSTEMS;
      dispatch(setIntakePayload({ ...p, currentSystems: { ...cs, systems: [...cs.systems, v] } }));
    },
    [dispatch]
  );

  const updateSystem = useCallback(
    (id: number, v: SystemData) => {
      const p = dataRef.current;
      const cs = p.currentSystems ?? EMPTY_CURRENT_SYSTEMS;
      dispatch(setIntakePayload({ ...p, currentSystems: { ...cs, systems: cs.systems.map((s) => (s.id === id ? v : s)) } }));
    },
    [dispatch]
  );

  const removeSystem = useCallback(
    (id: number) => {
      const p = dataRef.current;
      const cs = p.currentSystems ?? EMPTY_CURRENT_SYSTEMS;
      dispatch(setIntakePayload({ ...p, currentSystems: { ...cs, systems: cs.systems.filter((s) => s.id !== id) } }));
    },
    [dispatch]
  );

  const setNonSystemTools = useCallback(
    (v: NonSystemTools) => {
      const p = dataRef.current;
      const cs = p.currentSystems ?? EMPTY_CURRENT_SYSTEMS;
      dispatch(setIntakePayload({ ...p, currentSystems: { ...cs, nonSystemTools: v } }));
    },
    [dispatch]
  );

  const setPrimaryObjectOfWork = useCallback(
    (v: string[]) => {
      const keys = v as ('programs' | 'projects' | 'properties')[];
      const p = dataRef.current;
      const result: PrimaryObjectOfWorkItem[] = keys.map((key) => {
        const found = p.primaryObjectOfWork.find((item) => key in item);
        if (found) return found;
        if (key === 'projects') return { projects: createEmptyProjectsSubSections() };
        if (key === 'properties') return { properties: createEmptyPropertiesSubSection() };
        return { programs: createEmptyProgramSubSections() };
      });
      dispatch(setIntakePayload({ ...p, primaryObjectOfWork: result }));
    },
    [dispatch]
  );

  const updatePrimaryObjectOfWorkSubSection = useCallback(
    <K extends keyof ProgramSubSections>(sectionKey: PrimaryObjectOfWorkSectionKey, subSectionKey: K, value: ProgramSubSections[K]) => {
      const p = dataRef.current;
      dispatch(
        setIntakePayload({
          ...p,
          primaryObjectOfWork: p.primaryObjectOfWork.map((item): PrimaryObjectOfWorkItem => {
            if (sectionKey === 'programs' && 'programs' in item) {
              return { programs: { ...item.programs, [subSectionKey]: value } };
            }
            if (sectionKey === 'projects' && 'projects' in item) {
              return { projects: { ...item.projects, [subSectionKey]: value } };
            }
            if (sectionKey === 'properties' && 'properties' in item) {
              return { properties: { ...item.properties, [subSectionKey]: value } };
            }
            return item;
          }),
        })
      );
    },
    [dispatch]
  );

  const updateProgramSubSection = useCallback(
    <K extends keyof ProgramSubSections>(key: K, value: ProgramSubSections[K]) => {
      updatePrimaryObjectOfWorkSubSection('programs', key, value);
    },
    [updatePrimaryObjectOfWorkSubSection]
  );

  const updateProjectsSubSection = useCallback(
    <K extends keyof ProjectsSubSections>(key: K, value: ProjectsSubSections[K]) => {
      const p = dataRef.current;
      dispatch(
        setIntakePayload({
          ...p,
          primaryObjectOfWork: p.primaryObjectOfWork.map((item): PrimaryObjectOfWorkItem => {
            if ('projects' in item) return { projects: { ...item.projects, [key]: value } };
            return item;
          }),
        })
      );
    },
    [dispatch]
  );

  const updatePropertiesSubSection = useCallback(
    <K extends keyof PropertiesSubSections>(key: K, value: PropertiesSubSections[K]) => {
      const p = dataRef.current;
      dispatch(
        setIntakePayload({
          ...p,
          primaryObjectOfWork: p.primaryObjectOfWork.map((item): PrimaryObjectOfWorkItem => {
            if ('properties' in item) return { properties: { ...item.properties, [key]: value } };
            return item;
          }),
        })
      );
    },
    [dispatch]
  );

  const getPayload = useCallback((): IntakePayload => {
    return { ...dataRef.current };
  }, []);

  const getPayloadUpToStep = useCallback((stepIndex: number, stepKeys: string[]): IntakePayload => {
    const d = dataRef.current;
    const keysUpToStep = stepKeys.slice(0, stepIndex + 1);
    const hasObject = keysUpToStep.includes('object');

    const hasSubSection = (sectionKey: PrimaryObjectOfWorkSectionKey, subSectionKey: keyof ProgramSubSections) =>
      keysUpToStep.includes(`${sectionKey}-${subSectionKey}`);

    const emptyPrograms = createEmptyProgramSubSections();
    const emptyProjects = createEmptyProjectsSubSections();
    const emptyProperties = createEmptyPropertiesSubSection();

    const primaryObjectOfWork: PrimaryObjectOfWorkItem[] = d.primaryObjectOfWork.map((item) => {
      if ('programs' in item) {
        return {
          programs: {
            scoping: hasSubSection('programs', 'scoping') ? item.programs.scoping : emptyPrograms.scoping,
            currentSystems: hasSubSection('programs', 'currentSystems') ? item.programs.currentSystems : emptyPrograms.currentSystems,
            structure: hasSubSection('programs', 'structure') ? item.programs.structure : emptyPrograms.structure,
            painPoints: hasSubSection('programs', 'painPoints') ? item.programs.painPoints : emptyPrograms.painPoints,
          },
        };
      }
      if ('projects' in item) {
        return {
          projects: {
            scoping: hasSubSection('projects', 'scoping') ? item.projects.scoping : emptyProjects.scoping,
            currentSystems: hasSubSection('projects', 'currentSystems') ? item.projects.currentSystems : emptyProjects.currentSystems,
            structure: hasSubSection('projects', 'structure') ? item.projects.structure : emptyProjects.structure,
            painPoints: hasSubSection('projects', 'painPoints') ? item.projects.painPoints : emptyProjects.painPoints,
          },
        };
      }
      if ('properties' in item) {
        const hasPropertiesStep = (subKey: string) => keysUpToStep.includes(`properties-${subKey}`);
        return {
          properties: {
            tracking: hasPropertiesStep('scoping') ? item.properties.tracking : emptyProperties.tracking,
            operationalSystems: hasPropertiesStep('currentSystems')
              ? item.properties.operationalSystems
              : emptyProperties.operationalSystems,
            legacyConsolidation: hasPropertiesStep('legacyConsolidation')
              ? item.properties.legacyConsolidation
              : emptyProperties.legacyConsolidation,
            needs: hasPropertiesStep('needs') ? item.properties.needs : emptyProperties.needs,
            painPoints: hasPropertiesStep('needs') ? item.properties.painPoints : emptyProperties.painPoints,
          },
        };
      }
      return item;
    });

    return {
      entryContact: keysUpToStep.includes('contact') ? d.entryContact : EMPTY_ENTRY_CONTACT,
      roleContext: keysUpToStep.includes('role') ? d.roleContext : EMPTY_ROLE_CONTEXT,
      currentSystems: keysUpToStep.includes('systems') ? d.currentSystems : EMPTY_CURRENT_SYSTEMS,
      primaryObjectOfWork: hasObject ? primaryObjectOfWork : [],
    };
  }, []);

  return useMemo(
    () => ({
      data: {
        ...data,
        programs: data.primaryObjectOfWork.find((o) => 'programs' in o)?.programs,
        projects: data.primaryObjectOfWork.find((o) => 'projects' in o)?.projects,
        properties: data.primaryObjectOfWork.find((o) => 'properties' in o)?.properties,
      },
      updateEntryContact,
      updateRoleContext,
      addSystem,
      updateSystem,
      removeSystem,
      setNonSystemTools,
      setPrimaryObjectOfWork,
      updatePrimaryObjectOfWorkSubSection,
      updateProgramSubSection,
      updateProjectsSubSection,
      updatePropertiesSubSection,
      getPayload,
      getPayloadUpToStep,
    }),
    [
      data,
      updateEntryContact,
      updateRoleContext,
      addSystem,
      updateSystem,
      removeSystem,
      setNonSystemTools,
      setPrimaryObjectOfWork,
      updatePrimaryObjectOfWorkSubSection,
      updateProgramSubSection,
      updateProjectsSubSection,
      updatePropertiesSubSection,
      getPayload,
      getPayloadUpToStep,
    ]
  );
}
